import { PlusOutlined } from '@ant-design/icons';
import { Input as BaseInput, type InputRef, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface ButtonProps {
  $visible: boolean;
}

const Button = styled(Tag)<ButtonProps>`
  display: ${(p) => (p.$visible ? 'flex' : 'none')};
  align-items: center;
  background: #fff;
  border-style: dashed;
  font-size: 13px;
  gap: 4px;
  margin: 0;
  padding: 3px 9px;
  width: fit-content;
`;

const Input = styled(BaseInput)`
  height: 28px;
  width: 80px;
`;

const PlusIcon = styled(PlusOutlined)`
  margin-bottom: 2px;
`;

export interface NewTagButtonProps {
  onSubmit: (newTag: string) => void;
}

function NewTagButton({ onSubmit }: NewTagButtonProps) {
  const inputRef = useRef<InputRef>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newTagDraft, setNewTagDraft] = useState('');

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  // Blur and Escape both mean "never mind", and both discard the draft.
  // Keeping it would leave a half-entered tag with nothing on screen to
  // show for it, since the input unmounts on exit - returning later would
  // present text the user had forgotten typing. Preserving a draft would
  // mean not exiting at all, which is a heavy affordance for an input this
  // small.
  const exitEditMode = () => {
    setIsEditing(false);
    setNewTagDraft('');
  };

  const submitTag = () => {
    // Trimmed rather than merely length-checked: DocumentFolder.addTag
    // dedupes on exact string equality, so ' foo' and 'foo' would persist
    // as two tags.
    const newTag = newTagDraft.trim();
    if (newTag) {
      onSubmit(newTag);
    }
    exitEditMode();
  };

  return (
    <>
      <Button
        $visible={!isEditing}
        onClick={() => {
          setIsEditing(true);
        }}
      >
        <PlusIcon />
        New&nbsp;Tag
      </Button>

      {isEditing && (
        <Input
          type="text"
          ref={inputRef}
          onBlur={exitEditMode}
          onChange={(e) => {
            setNewTagDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            // rc-input fires onPressEnter first and then always calls this,
            // so the two coexist rather than clobber each other.
            if (e.key === 'Escape') {
              // stopPropagation to let Escape cancel tag entry, but not
              // dismiss the enclosing modal. Scoped only to the key so as
              // not to interfere with other keyboard handling by antd.
              //
              // Note this input unmounts on exit, which drops focus to
              // document.body (and not the enclosing modal), meaning a
              // double tap of escape will not dismiss the modal - it has to
              // be given focus again. Explicitly focusing something inside
              // the modal here would restore it; left alone for now.
              e.stopPropagation();
              exitEditMode();
            }
          }}
          onPressEnter={submitTag}
          value={newTagDraft}
        />
      )}
    </>
  );
}

export default NewTagButton;
